import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { getCancellationDocumentTypeLabel } from 'src/app/_enums/cancellation/cancellation-document-type.enum';
import { CancellationFlowStatus } from 'src/app/_enums/cancellation/cancellation-flow-status.enum';
import { ProposalState } from 'src/app/_enums/proposal.enums';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { fmtDate } from 'src/app/_helpers/utils';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { DocumentService } from 'src/app/_services/document.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import {
  CancellationFlowDetailsResponse,
  CancellationFlowDocument,
} from 'src/app/_types/cancellation.types';
import { DocumentModel } from 'src/app/_types/document.types';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { CancellationDocumentModalComponent } from 'src/app/shared/components/cancellation-document-modal/cancellation-document-modal.component';
import { PdfViewerModalComponent } from 'src/app/shared/components/pdf-viewer-modal/pdf-viewer-modal.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';
import { environment } from 'src/environments/environment';

import { TaskDetailsService } from '../../task-details.service';
import { TaskCancellationsService } from '../task-cancellations/task-cancellations.service';

/** Where a policy document may be filed: an accepted offer, or the policy it became. */
const POLICY_DOCUMENT_STATES: ReadonlySet<ProposalState | undefined> = new Set([
  ProposalState.OFFER_ACCEPTED,
  ProposalState.SIGNED_DOCS,
  ProposalState.POLICY_CREATED,
]);

/**
 * « Documents contractuels »: the policy's documents, then those of the
 * opened flow's cancellations, with one « Ajouter un document » for whichever
 * of the two can take one — the policy while its offer is accepted, the
 * running cancellation while it runs.
 */
@Component({
  selector: 'app-task-policy-documents',
  standalone: true,
  imports: [
    CommonModule,
    ExpansionPanelComponent,
    PdfViewerModalComponent,
    ActionModalComponent,
    CancellationDocumentModalComponent,
    IconsModule,
  ],
  templateUrl: './task-policy-documents.component.html',
  styleUrl: './task-policy-documents.component.css',
})
export class TaskPolicyDocumentsComponent implements OnInit, OnDestroy {
  private readonly taskDetailsService = inject(TaskDetailsService);

  private readonly userAuthService = inject(UserAuthService);

  private readonly documentService = inject(DocumentService);

  private readonly cancellationService = inject(CancellationService);

  private readonly toastr = inject(ToastrService);

  protected readonly store = inject(TaskCancellationsService);

  DATE_FORMAT = environment.DATE_FORMAT;

  signedOfferDocument: DocumentModel | null = null;

  private stateSubscription?: Subscription;

  private documentSubscription?: Subscription;

  @ViewChild('pdfViewer')
  viewer!: PdfViewerModalComponent;

  @ViewChild('deleteModal')
  private readonly deleteModal?: ActionModalComponent;

  private toRemove: {
    cancellationId: string;
    documentId: number;
  } | null = null;

  protected deleting = false;

  ngOnInit(): void {
    this.stateSubscription = this.taskDetailsService.state$.subscribe(() => {
      this.loadSignedOfferDocument();
    });
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
    this.documentSubscription?.unsubscribe();
  }

  protected get proposal(): ProposalDetails | null {
    return this.taskDetailsService.state.policy;
  }

  /** The proposal a policy document can be filed on, if its state allows one. */
  protected get policyProposalId(): string | null {
    const { proposal } = this;
    return proposal && POLICY_DOCUMENT_STATES.has(proposal.proposalState)
      ? proposal.proposalId
      : null;
  }

  protected isCalledOff(
    cancellation: CancellationFlowDetailsResponse
  ): boolean {
    return cancellation.status === CancellationFlowStatus.CANCELLED;
  }

  protected readonly typeLabel = getCancellationDocumentTypeLabel;

  /** The date its panel is titled with. */
  protected cancellationLabel(
    cancellation: CancellationFlowDetailsResponse
  ): string {
    return $localize`Résiliation du ${fmtDate(cancellation.createdOn)}:date:`;
  }

  /** Filed on the proposal: the policy's documents and the log are re-read. */
  protected onPolicyDocumentAdded(): void {
    this.taskDetailsService.refreshTaskDetails();
    this.taskDetailsService.notifyChanged();
  }

  protected confirmDelete(
    cancellation: CancellationFlowDetailsResponse,
    document: CancellationFlowDocument
  ): void {
    this.toRemove = {
      cancellationId: cancellation.id,
      documentId: document.documentId,
    };
    this.deleting = false;
    this.deleteModal?.openModal();
  }

  /** The file itself is kept; only the request forgets it, and its timeline says so. */
  protected remove(): void {
    const target = this.toRemove;
    if (!target || this.deleting) return;
    this.deleting = true;
    this.cancellationService
      .deleteDocument(target.cancellationId, target.documentId)
      .subscribe({
        next: (res) => {
          this.deleting = false;
          this.toastr.success($localize`Document supprimé`);
          this.store.changed(res.data);
          this.deleteModal?.closeModal();
        },
        error: (err) => {
          this.deleting = false;
          this.toastr.error(
            getApiErrorMessage(
              err,
              $localize`Une erreur est survenue. Veuillez réessayer`
            )
          );
        },
      });
  }

  private loadSignedOfferDocument(): void {
    const signedOfferId = this.proposal?.signedOfferId;
    if (!signedOfferId) {
      this.signedOfferDocument = null;
      return;
    }

    this.documentSubscription?.unsubscribe();
    this.documentSubscription = this.documentService
      .getDocument(Number(signedOfferId))
      .subscribe({
        next: (res) => {
          this.signedOfferDocument = res.data ?? null;
        },
        error: () => {
          this.signedOfferDocument = null;
        },
      });
  }

  getSignedOfferDocumentKey(): string {
    return (
      this.signedOfferDocument?.key ?? this.signedOfferDocument?.docUrl ?? ''
    );
  }

  viewFile(key: string): void {
    if (key.includes('https://')) {
      this.setFileUrl(key);
      return;
    }
    const failed = () =>
      this.toastr.error($localize`Impossible d'ouvrir le document`);
    this.userAuthService.getPresignedUrl(key).subscribe({
      next: (res) => {
        if (res.status === 'SUCCESS') this.setFileUrl(res.data.preSignedUrl);
        else failed();
      },
      error: failed,
    });
  }

  private setFileUrl(url: string): void {
    this.viewer.pdfFileUrl = url;
    this.viewer.openModal();
  }
}
